import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../shadcn/ui/dialog";
import { Button } from "../../shadcn/ui/button";
import { Textarea } from "../../shadcn/ui/textarea";
import axios from '../../axios';
import config from "../../config";
import { Label } from "../../shadcn/ui/label";
import { Skeleton } from "../../shadcn/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../shadcn/ui/tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { TooltipProvider } from "../../shadcn/ui/tooltip";

const MediaViewDialog = ({ isOpen, onClose, mediaItem, onSave }) => {
    const [mediaAttributes, setMediaAttributes] = useState({
        title: "",
        description: "",
        alt: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    // const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);


    // Handle modal opening and initialize states
    useEffect(() => {
        if (isOpen && mediaItem) {
            // Reset states when modal is opened with a new item
            setMediaAttributes({
                title: "",
                description: "",
                alt: "",
            });
            setIsLoading(true);

            // Simulate loading and set the attributes
            setTimeout(() => {
                if (mediaItem.attributes) {
                    setMediaAttributes({
                        title: mediaItem.attributes.title || "",
                        description: mediaItem.attributes.description || "",
                        alt: mediaItem.attributes.alt || "",
                    });
                }
                setIsLoading(false);
            }, 800); // Simulate loading time
        }
    }, [isOpen, mediaItem]);

    // Update the attributes in local state
    const handleDetailChange = (field, value) => {
        setMediaAttributes((prevAttributes) => ({
            ...prevAttributes,
            [field]: value,
        }));
    };

    // const handleViewMedia = (mediaItem) => {
    //     setSelectedMedia(mediaItem);
    //     setIsViewDialogOpen(true);
    //   };
    
    //   const handleCloseViewDialog = () => {
    //     setIsViewDialogOpen(false);
    //     setSelectedMedia(null);
    //   };

    // Save the updated media attributes
    const saveMediaAttributes = async () => {
        try {
            console.log(mediaItem);
            const response = await axios.put(
                `/api/media/${mediaItem.attributes.id}/attributes`,
                mediaAttributes
            );

            const updatedMediaItem = {
                ...mediaItem,
                attributes: {
                    ...mediaItem.attributes,
                    ...response.data,
                },
            };

            setMediaAttributes(updatedMediaItem.attributes);

            // Notify the parent component
            if (onSave) {
                onSave(updatedMediaItem);
            }

            console.log("Media attributes saved successfully:", response.data);
            onClose(); // Close the dialog after saving
        } catch (error) {
            console.error("Error saving media attributes:", error);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            {isOpen && mediaItem && (
                <TooltipProvider>
                    <DialogContent className="sm:max-w-[900px] w-full overflow-hidden">
                        <DialogHeader>
                            <DialogTitle>Επεξεργασία χαρακτηριστικών πολυμέσων</DialogTitle>
                        </DialogHeader>
                        <div>
                            <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10 overflow-auto">
                                {/* Display the media file or skeleton */}
                                <div className="flex-shrink-0 w-full md:w-[400px] h-[300px] md:h-[400px] bg-gray-100 rounded overflow-hidden">
                                    {isLoading ? (
                                        <Skeleton className="w-full h-full" />
                                    ) : (
                                        <img
                                            src={config.apiUrl + mediaItem.file_path}
                                            alt="media"
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>

                                {/* Input fields for media attributes */}
                                <div className="flex-grow w-full">
                                    {isLoading ? (
                                        <>
                                            <Skeleton className="h-10 w-full mb-4" />
                                            <Skeleton className="h-20 w-full mb-4" />
                                            <Skeleton className="h-10 w-full mb-4" />
                                        </>
                                    ) : (
                                        <>
                                            {/* Title Input with Tooltip */}
                                            <Label htmlFor="title" className="block text-left mb-2 flex items-center">
                                                Τίτλος
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <InformationCircleIcon className="h-5 w-5 text-gray-500 ml-2 cursor-pointer" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Ο τίτλος είναι το όνομα του πολυμέσου. Χρησιμοποιήστε έναν σύντομο και περιγραφικό τίτλο που βοηθά στην αναγνώριση του αρχείου.
                                                    </TooltipContent>
                                                </Tooltip>
                                            </Label>
                                            <div className="relative">
                                                <input
                                                    id="title"
                                                    type="text"
                                                    placeholder="Τίτλος"
                                                    value={mediaAttributes.title}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value.length <= 25) {
                                                            handleDetailChange("title", value);
                                                        }
                                                    }}
                                                    className="h-10 border rounded p-2 w-full mb-4"
                                                />
                                                <span className="absolute bottom-2 right-2 text-sm text-gray-500">
                                                    {mediaAttributes.title.length}/25
                                                </span>
                                            </div>

                                            {/* Description Input with Tooltip */}
                                            <Label htmlFor="description" className="block text-left mb-2 flex items-center">
                                                Περιγραφή
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <InformationCircleIcon className="h-5 w-5 text-gray-500 ml-2 cursor-pointer" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Η περιγραφή παρέχει περισσότερες λεπτομέρειες για το πολυμέσο. Περιγράψτε το περιεχόμενο ή τη χρήση του αρχείου ώστε να είναι εύκολα κατανοητό.
                                                    </TooltipContent>
                                                </Tooltip>
                                            </Label>
                                            <div className="relative">
                                                <Textarea
                                                    id="description"
                                                    placeholder="Περιγραφή"
                                                    value={mediaAttributes.description}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value.length <= 120) {
                                                            handleDetailChange("description", value);
                                                        }
                                                    }}
                                                    className="border rounded p-2 w-full mb-4"
                                                />
                                                <span className="absolute bottom-2 right-2 text-sm text-gray-500">
                                                    {mediaAttributes.description.length}/120
                                                </span>
                                            </div>

                                            {/* Alt Text Input with Tooltip */}
                                            <Label htmlFor="alt" className="block text-left mb-2 flex items-center">
                                                Εναλλακτικό κείμενο
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <InformationCircleIcon className="h-5 w-5 text-gray-500 ml-2 cursor-pointer" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Το εναλλακτικό κείμενο χρησιμοποιείται για προσβασιμότητα και SEO. Περιγράψτε το περιεχόμενο της εικόνας για χρήστες με προβλήματα όρασης.
                                                    </TooltipContent>
                                                </Tooltip>
                                            </Label>
                                            <div className="relative">
                                                <input
                                                    id="alt"
                                                    type="text"
                                                    placeholder="Εναλλακτικό κείμενο"
                                                    value={mediaAttributes.alt}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value.length <= 25) {
                                                            handleDetailChange("alt", value);
                                                        }
                                                    }}
                                                    className="h-10 border rounded p-2 w-full"
                                                />
                                                <span className="absolute bottom-2 right-2 text-sm text-gray-500">
                                                    {mediaAttributes.alt.length}/25
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Save button */}
                            <div className="mt-6 flex justify-end">
                                <Button
                                    onClick={saveMediaAttributes}
                                    disabled={!mediaItem || isLoading}
                                >
                                    Αποθήκευση
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </TooltipProvider>
            )}
        </Dialog>
    );
};

export default MediaViewDialog;
